import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaArrowRight, FaCalendarAlt, FaGlobeAmericas } from "react-icons/fa";
import vectorBg from "../../../assets/images/services-vector-bg.svg";
import "./FeaturedResearchSection.scss";

const FeaturedResearchSection = () => {
  const navigate = useNavigate();
  const { reports } = useSelector((state) => state.researchReports);

  const [activeBatch, setActiveBatch] = useState(0);

  const getReportCategory = (report) => {
    return (
      report.category ||
      report.Industry ||
      report.industry ||
      "Market Insights"
    ).trim();
  };

  const getReportRegion = (report) => {
    const regionBase =
      report.region || report.RegionsCovered || report.Region || "Global";

    const country = report.country;

    return country && regionBase !== "Global"
      ? `${regionBase} (${country})`
      : regionBase;
  };

  const normalizeValue = (value) => {
    return String(value || "")
      .trim()
      .toLowerCase();
  };

  const latestIndustryReports = useMemo(() => {
    if (!reports || reports.length === 0) {
      return [];
    }

    const publishedReports = reports.filter(
      (report) => !report.status || report.status.toLowerCase() === "published",
    );

    const sortedReports = [...publishedReports].sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      }

      return (b.id || 0) - (a.id || 0);
    });

    const latestByIndustry = new Map();

    for (const report of sortedReports) {
      const category = getReportCategory(report);
      const categoryKey = normalizeValue(category);

      if (!latestByIndustry.has(categoryKey)) {
        latestByIndustry.set(categoryKey, report);
      }
    }

    return Array.from(latestByIndustry.values());
  }, [reports]);

  const reportBatches = useMemo(() => {
    if (latestIndustryReports.length === 0) {
      return [];
    }

    const totalIndustries = latestIndustryReports.length;

    if (totalIndustries <= 6) {
      return [latestIndustryReports.slice(0, 6)];
    }

    const batches = [];

    const numberOfBatches = Math.ceil(totalIndustries / 6);

    for (let batchIndex = 0; batchIndex < numberOfBatches; batchIndex++) {
      const batch = [];

      for (let cardIndex = 0; cardIndex < 6; cardIndex++) {
        const industryIndex = (batchIndex * 6 + cardIndex) % totalIndustries;

        const report = latestIndustryReports[industryIndex];

        if (report) {
          batch.push(report);
        }
      }

      if (batch.length > 0) {
        batches.push(batch);
      }
    }

    return batches;
  }, [latestIndustryReports]);

  useEffect(() => {
    setActiveBatch(0);
  }, [latestIndustryReports.length]);

  useEffect(() => {
    if (reportBatches.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setActiveBatch((previousBatch) => {
        return (previousBatch + 1) % reportBatches.length;
      });
    }, 8000);

    return () => {
      clearInterval(interval);
    };
  }, [reportBatches.length]);

  const trendingReports = useMemo(() => {
    if (!reports || reports.length === 0) {
      return [
        {
          id: "loading-1",
          title: "Loading Market Insights...",
          category: "Loading",
          cagr: "--%",
          period: "----",
        },
        {
          id: "loading-2",
          title: "Loading Market Insights...",
          category: "Loading",
          cagr: "--%",
          period: "----",
        },
        {
          id: "loading-3",
          title: "Loading Market Insights...",
          category: "Loading",
          cagr: "--%",
          period: "----",
        },
        {
          id: "loading-4",
          title: "Loading Market Insights...",
          category: "Loading",
          cagr: "--%",
          period: "----",
        },
        {
          id: "loading-5",
          title: "Loading Market Insights...",
          category: "Loading",
          cagr: "--%",
          period: "----",
        },
        {
          id: "loading-6",
          title: "Loading Market Insights...",
          category: "Loading",
          cagr: "--%",
          period: "----",
        },
      ];
    }

    const currentBatch =
      reportBatches.length > 0
        ? reportBatches[activeBatch % reportBatches.length]
        : [];

    return currentBatch.map((report) => {
      const forecastPeriod =
        report.forecastPeriod ||
        report.forecast_period ||
        report.ForecastPeriod;

      const studyPeriod =
        report.StudyPeriod ||
        report.studyPeriod ||
        forecastPeriod ||
        "2024 - 2032";

      return {
        id: report.slug || report.id,

        title: report.title
          ? report.title.replace(/\s*-\s*$/, "")
          : "Market Report",

        category: getReportCategory(report),

        publicationDate: report.publicationDate || "",

        region: getReportRegion(report),

        summary: report.summary || "",

        cagr: report.cagr || report.CAGR || "XX%",

        period: studyPeriod,

        marketSize:
          report.marketSizeForecast ||
          report.market_size_forecast ||
          report.marketSize ||
          "N/A",

        slug: report.slug || report.id,

        rawReport: report,
      };
    });
  }, [reports, reportBatches, activeBatch]);

  return (
    <section
      className="trending-section py-3 position-relative overflow-hidden"
      style={{
        backgroundImage: `url(${vectorBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <div
        className="position-absolute top-0 start-0 w-100 h-100"
        style={{
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-30%",
            right: "5%",
            width: "800px",
            height: "800px",
            background:
              "radial-gradient(circle, rgba(108, 160, 255, 0.15) 0%, transparent 70%)",
            borderRadius: "50%",
            animation: "driftCircle1 20s ease-in-out infinite alternate",
          }}
        ></div>

        <div
          style={{
            position: "absolute",
            bottom: "-20%",
            left: "-10%",
            width: "600px",
            height: "600px",
            background:
              "radial-gradient(circle, rgba(138, 43, 226, 0.14) 0%, transparent 70%)",
            borderRadius: "50%",
            animation:
              "driftCircle2 18s ease-in-out infinite alternate-reverse",
          }}
        ></div>
      </div>

      <div
        className="container py-3 position-relative featured-research-container"
        style={{
          zIndex: 1,
        }}
      >
        <div className="text-center mb-3 animate-fade-up">
          <h2
            className="fw-bold display-6 text-dark mb-4 section-title"
            style={{
              lineHeight: "1.2",
            }}
          >
            Featured Research Reports
          </h2>

          <p className="text-muted lead section-subtitle">
            Explore some of our latest syndicated research covering emerging
            industries, high-growth markets, evolving technologies, and
            strategic business opportunities.
          </p>
        </div>

        <div className="featured-research-wrapper">
          <div
            key={activeBatch}
            className="row g-4 justify-content-center mt-2 featured-research-batch"
          >
            {trendingReports.map((report, index) => {
              const isLoading = report.id.toString().startsWith("loading");

              return (
                <div
                  className="col-md-4 featured-research-card"
                  style={{
                    animationDelay: `${index * 0.08}s`,
                  }}
                  key={report.id}
                >
                  <div
                    className={`trending-card rounded shadow h-100 d-flex flex-column transition-hover ${
                      isLoading ? "" : "cursor-pointer"
                    }`}
                    onClick={() => {
                      if (!isLoading) {
                        const targetReport = report.rawReport || report;
                        navigate(`/reports/${report.slug}`, {
                          state: {
                            report: targetReport,
                          },
                        });
                      }
                    }}
                    style={{
                      cursor: isLoading ? "default" : "pointer",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      className="w-100 text-left py-2 px-3"
                      style={{
                        backgroundColor: "#053b84",
                        color: "#ffffff",
                      }}
                    >
                      <span
                        className="fw-bold small text-truncate d-block"
                        title={report.category}
                        style={{
                          letterSpacing: "0.5px",
                        }}
                      >
                        {report.category}
                      </span>
                    </div>

                    <div className="p-4 pt-3 d-flex flex-column flex-grow-1">
                      <h4
                        className="h5 fw-bold mb-2 text-dark trending-card-title"
                        style={{
                          lineHeight: "1.5",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {report.title}
                      </h4>

                      <p
                        className="text-muted small mb-3 flex-grow-1"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: "1.6",
                        }}
                      >
                        {report.summary}
                      </p>

                      {report.publicationDate && (
                        <div className="d-flex flex-wrap gap-3 text-muted small mb-3">
                          <span className="d-flex align-items-center gap-1">
                            <FaCalendarAlt
                              className="text-secondary"
                              opacity={0.7}
                            />

                            {report.publicationDate}
                          </span>
                        </div>
                      )}

                      <div
                        className="d-flex align-items-center gap-2 text-muted small mb-2 text-nowrap"
                        style={{
                          lineHeight: "1.6",
                          fontSize: "0.8rem",
                          overflow: "hidden",
                        }}
                      >
                        <span className="d-flex align-items-center gap-1 flex-shrink-0">
                          <FaGlobeAmericas
                            className="text-secondary"
                            opacity={0.7}
                          />

                          <strong>{report.region}</strong>
                        </span>

                        <span className="text-secondary opacity-50 flex-shrink-0">
                          |
                        </span>

                        <span className="text-truncate">
                          <strong>Market Size:</strong> {report.marketSize}
                        </span>
                      </div>

                      <div
                        className="border-top pt-2 mt-auto w-100 d-flex flex-row justify-content-between align-items-center"
                        style={{
                          gap: "10px",
                        }}
                      >
                        <span
                          className="text-muted small"
                          style={{
                            fontSize: "0.8rem",
                          }}
                        >
                          <strong>CAGR:</strong> <span>{report.cagr}</span>
                        </span>

                        <span
                          className="fw-bold small d-flex align-items-center trending-card-link text-nowrap"
                          style={{
                            color: "#053b84",
                            flexShrink: 0,
                          }}
                        >
                          View Report
                          <FaArrowRight className="ms-2" size={12} />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="text-center mt-5 animate-fade-up delay-3">
          <Link
            to="/reports"
            className="btn px-4 py-2 rounded-pill shadow-sm"
            style={{
              backgroundColor: "#053b84",
              color: "#fff",
            }}
          >
            Read More Insights
            <FaArrowRight className="ms-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedResearchSection;
